import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { Box, Button, Grid } from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import React from 'react';
import { AtCircularProgress } from '../../../components/AtCircularProgress';
import { updateTemplateDynamic } from '../../../services/dynamicPanels';
import {
  DYNAMIC_VIEW_REDUCER_ACTIONS,
  DynamicViewStateContext,
  DynamicViewDispatchContext,
  dynamicViewReducer,
  initialState,
} from '../context/dynamic-view-context';
import { useDynamicPanelsUser } from '../hooks/useDynamicPanelsUser';
import { NewSlotCard } from './card/NewSlotCard';
import { SortableItem } from './dnd/SortableItem';
import { FavouritesDashboard } from './layouts-management/FavouritesDashboard';
import { PanelsSection } from './layouts-management/PanelsSection';
import { GlobalFilter } from './main/GlobalFilter';
import { useTranslation } from 'react-i18next';
import { TutorialDialog } from './tutorial/TutorialDialog';

/**
 * @fileoverview Componente principal para el layout de paneles dinámicos.
 *
 * Muestra el dashboard dinámico donde los usuarios pueden organizar, añadir
 * y eliminar widgets. Gestiona el estado global del panel dinámico, incluyendo
 * la funcionalidad de arrastrar y soltar (Drag & Drop), guardado automático
 * del layout y visualización de dashboards favoritos.
 *
 * @module PanelsLayout
 * @requires React
 * @requires @dnd-kit/core
 * @requires @dnd-kit/sortable
 */

/**
 * Componente PanelsLayout.
 *
 * Renderiza la rejilla de paneles (widgets) ordenables.
 *
 * Funcionalidades principales:
 * - Provee el contexto `DynamicViewContext`.
 * - Gestiona la carga de dashboards favoritos.
 * - Implementa Drag & Drop para reordenar widgets.
 * - Guarda automáticamente los cambios en el layout en el servidor.
 * - Muestra alertas cuando no hay dashboards o paneles.
 *
 * @returns {React.ReactElement} El layout completo de paneles dinámicos.
 * 
 * @author David Maldonado
 * (c) Servicios de Monitoreo S.A. - Santiago, Chile
 */
export function PanelsLayout({ initialPanel = null, hideControls = false }) {
  const { t } = useTranslation();
  const [state, dispatch] = React.useReducer(dynamicViewReducer, initialState);
  const [isPanelsOpen, setIsPanelsOpen] = React.useState(false);

  React.useEffect(() => {
    if (initialPanel && initialPanel.panel_dinamico_usuario_id) {
      dispatch({
        type: DYNAMIC_VIEW_REDUCER_ACTIONS.SET_ACTIVE_LAYOUT,
        payload: initialPanel.estructura_panel_dinamico || [],
      });

      dispatch({
        type: DYNAMIC_VIEW_REDUCER_ACTIONS.SET_GLOBAL_OBJECTIVE,
        payload: {
          objetivoId: initialPanel.objetivo_id || null,
          applyToAll: initialPanel.apply_to_all_objetivo_id ?? false,
        },
      });

      dispatch({
        type: DYNAMIC_VIEW_REDUCER_ACTIONS.SET_ACTIVE_LAYOUT_ID,
        payload: initialPanel.panel_dinamico_usuario_id,
      });
    }
  }, [initialPanel]);

  const { data: dashboardsFavoritos, isLoading } = useDynamicPanelsUser({
    enabled: true,
    favourites: true,
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        delay: 150,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  /**
   * Manejador para el evento de finalización del arrastre.
   *
   * Reordena el layout activo cuando un elemento es arrastrado a una nueva posición.
   * Intercambia también las propiedades de columnas ('cols') entre los elementos para
   * mantener la consistencia visual si tienen diferentes tamaños.
   *
   * @param {Object} event - Evento de drag end de dnd-kit.
   * @param {Object} event.active - El elemento que se está arrastrando.
   * @param {Object} event.over - El elemento sobre el que se soltó.
   */
  function handleDragEnd(event) {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = state.activeLayout.findIndex(
        (item) => item.id === active.id
      );
      const newIndex = state.activeLayout.findIndex(
        (item) => item.id === over.id
      );
      const newIndexCols = state.activeLayout[newIndex]['cols'];
      const oldIndexCols = state.activeLayout[oldIndex]['cols'];
      state.activeLayout[newIndex]['cols'] = oldIndexCols;
      state.activeLayout[oldIndex]['cols'] = newIndexCols;

      const newlayout = arrayMove(state.activeLayout, oldIndex, newIndex);

      dispatch({
        type: DYNAMIC_VIEW_REDUCER_ACTIONS.UPDATE_TEMPLATE,
        payload: newlayout,
      });
    }
  }

  const memoizedState = React.useMemo(() => state, [state]);

  const memoizedDispatch = React.useMemo(() => dispatch, []);

  /**
   * Efecto para guardar automáticamente los cambios en el layout.
   *
   * Este efecto monitorea los cambios en `state.activeLayout` y persiste la configuración
   * actualizada en el servidor. Se utiliza `JSON.stringify` en la dependencia para
   * detectar cambios profundos en la estructura del layout.
   *
   * !TODO: Buscar una mejor forma de detectar cambios sin necesidad de serializar todo el objeto.
   * 
   * @function useEffect_saveLayout
   */
  React.useEffect(() => {
    
    async function saveLayout() {
      await updateTemplateDynamic({
        panelId: state.activeLayoutId,
        data: state.activeLayout,
        objetivoId: state.globalObjective?.objetivoId ?? null
      });
    }
    if (state.activeLayoutId) {
      saveLayout().catch((error) => {
        console.error('Error saving layout:', error);
      });
    }
  }, [JSON.stringify(state.activeLayout)]);

  if (isLoading) {
    return <AtCircularProgress />;
  }

  return (
    <DynamicViewStateContext.Provider value={memoizedState}>
      <DynamicViewDispatchContext.Provider value={memoizedDispatch}>
        {!hideControls && (
          <Box
            marginBottom={3}
            display="flex"
            justifyContent="space-between"
            flexWrap="wrap"
            gridGap={5}
          >
            <TutorialDialog />
            {state.activeLayoutId ? <GlobalFilter /> : <div />}
            <Button
              onClick={() => setIsPanelsOpen(true)}
              variant="contained"
              color="primary"
            >
              {t('dynamicPanel.panelsSection.myDashboards')}
            </Button>
            <PanelsSection isPanelsOpen={isPanelsOpen} handleClosePanels={() => setIsPanelsOpen(false)}/>
          </Box>
        )}
        {!hideControls &&
          !state.activeLayoutId &&
          dashboardsFavoritos &&
          dashboardsFavoritos.length > 0 ? (
          <FavouritesDashboard dashboardsFavoritos={dashboardsFavoritos} />
        ) : null}
        {!hideControls &&
          !state.activeLayoutId &&
          dashboardsFavoritos?.length === 0 ? (
          <Alert severity="info">
            <AlertTitle>{t('panelsLayout.noFavoriteDashboards')}</AlertTitle>
            {t('panelsLayout.markFavoriteDashboards')}
          </Alert>
        ) : null}
        {!hideControls &&
          state.activeLayoutId &&
          state.activeLayout.length === 0 ? (
          <Alert severity="info" style={{ marginBottom: 10 }}>
            <AlertTitle>{t('panelsLayout.noPanelsInLayout')}</AlertTitle>
            {t('panelsLayout.addPanelsToLayout')}
          </Alert>
        ) : null}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={state.activeLayout.map((item) => item.id)}
            strategy={rectSortingStrategy}
          >
            <Grid container spacing={2}>
              {state.activeLayout.map((slot) => (
                <SortableItem key={slot.id} slot={slot} />
              ))}
              <NewSlotCard />
            </Grid>
          </SortableContext>
        </DndContext>
      </DynamicViewDispatchContext.Provider>
    </DynamicViewStateContext.Provider>
  );
}
